import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Loader2, Send, Download, Camera, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import EquipoSelect from "@/components/EquipoSelect";

import { logoColor as LOGO_URL } from "@/lib/logos";
const TIPOS_EVENTO = ["Falla mayor", "Siniestro", "Peritaje técnico", "Evaluación de equipo", "Incidente", "Otro"];
const SECCIONES = [
  ["Descripción del evento *", "descripcion", "Describe qué ocurrió, cuándo y en qué circunstancias..."],
  ["Antecedentes", "antecedentes", "Historial relevante del equipo o del evento..."],
  ["Análisis y diagnóstico técnico *", "diagnostico", "Análisis técnico del problema y sus causas..."],
  ["Conclusiones *", "conclusiones", "Conclusiones del equipo Odontofix..."],
  ["Recomendaciones", "recomendaciones", "Recomendaciones y próximos pasos sugeridos..."],
];

export default function InformeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [estado, setEstado] = useState("borrador");
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSnapshotRef = useRef("");
  const stateRef = useRef(null);
  const reportIdRef = useRef(reportId);
  const inFlightSaveRef = useRef(null);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  const [inf, setInf] = useState({
    cliente_id: "", cliente_nombre: "", cliente_rut: "", contacto_nombre: "", contacto_email: "",
    sucursal: "", direccion: "", marca: "", modelo: "", numero_serie: "", numero_box: "",
    titulo: "", tipo_evento: "", fecha_evento: new Date().toISOString().split("T")[0],
    descripcion: "", antecedentes: "", diagnostico: "", conclusiones: "", recomendaciones: "",
    imagenes: [],
  });

  const ui = (f, v) => setInf((p) => ({ ...p, [f]: v }));

  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/informes/${id}`).then((r) => {
      const d = r.data;
      const loaded = {
        cliente_id: d.cliente_id || "", cliente_nombre: d.cliente_nombre || "",
        cliente_rut: d.cliente_rut || "", contacto_nombre: d.contacto_nombre || "",
        contacto_email: d.contacto_email || "", sucursal: d.sucursal || "", direccion: d.direccion || "",
        marca: d.marca || "", modelo: d.modelo || "", numero_serie: d.numero_serie || "",
        numero_box: d.numero_box || "", titulo: d.titulo || "", tipo_evento: d.tipo_evento || "",
        fecha_evento: d.fecha_evento || "", descripcion: d.descripcion || "",
        antecedentes: d.antecedentes || "", diagnostico: d.diagnostico || "",
        conclusiones: d.conclusiones || "", recomendaciones: d.recomendaciones || "",
        imagenes: d.imagenes || [],
      };
      setInf(loaded);
      lastSnapshotRef.current = JSON.stringify(loaded);
      setFolio(d.folio); setReportId(d.id); setEstado(d.estado || "borrador");
      if (d.estado === "completado" && user?.cargo !== "admin") setReadOnly(true);
    }).catch(() => toast.error("Error al cargar el informe"));
  }, [id, api, user?.cargo]);

  const selectClient = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    setInf((p) => ({
      ...p, cliente_id: clientId, cliente_nombre: c.nombre, cliente_rut: c.rut || "",
      contacto_nombre: c.contacto_nombre || "", contacto_email: c.contacto_email || "",
      sucursal: "", direccion: "",
    }));
  };
  const selectedClient = clients.find((c) => c.id === inf.cliente_id);

  const payload = () => ({ ...inf });

  const save = async ({ silent = false } = {}) => {
    if (readOnly) return reportId;
    if (!inf.cliente_nombre) { if (!silent) toast.error("Selecciona un cliente"); return null; }
    if (inFlightSaveRef.current) {
      try { await inFlightSaveRef.current; } catch (_) {}
    }
    const p = (async () => {
      setSaving(true);
      try {
        if (reportId) {
          await api.put(`/api/informes/${reportId}`, payload());
          if (!silent) toast.success("Informe guardado");
          setLastSavedAt(Date.now());
          lastSnapshotRef.current = JSON.stringify(inf);
          return reportId;
        }
        const r = await api.post("/api/informes", payload());
        setReportId(r.data.id); setFolio(r.data.folio);
        if (!silent) toast.success(`Informe creado: ${r.data.folio}`);
        setLastSavedAt(Date.now());
        lastSnapshotRef.current = JSON.stringify(inf);
        return r.data.id;
      } catch (e) {
        if (!silent) toast.error(e.response?.data?.detail || "Error al guardar");
        return null;
      } finally { setSaving(false); }
    })();
    inFlightSaveRef.current = p;
    try { return await p; }
    finally { if (inFlightSaveRef.current === p) inFlightSaveRef.current = null; }
  };

  // Auto-guardado del borrador (mismo patrón que MP).
  useEffect(() => { stateRef.current = inf; }, [inf]);
  useEffect(() => {
    if (readOnly || saving || sending) return;
    if (!inf.cliente_nombre) return;
    const snap = JSON.stringify(inf);
    if (snap === lastSnapshotRef.current) return;
    const t = setTimeout(() => { save({ silent: true }); }, 2000);
    return () => clearTimeout(t);
  }, [inf, readOnly, saving, sending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush al cerrar la pestaña.
  useEffect(() => {
    const onBeforeUnload = () => {
      const rid = reportIdRef.current;
      const s = stateRef.current;
      if (!rid || !s || !s.cliente_nombre) return;
      const snap = JSON.stringify(s);
      if (snap === lastSnapshotRef.current) return;
      const token = localStorage.getItem("odf_token");
      const url = `${api.defaults.baseURL || ""}/api/informes/${rid}`;
      try {
        fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json",
                     ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(s),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* best effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [api]);

  const requiredMissing = () => {
    const m = [];
    if (!inf.cliente_nombre) m.push("Cliente");
    if (!inf.titulo) m.push("Asunto del informe");
    if (!inf.tipo_evento) m.push("Tipo de evento");
    if (!inf.descripcion) m.push("Descripción del evento");
    if (!inf.diagnostico) m.push("Análisis y diagnóstico");
    if (!inf.conclusiones) m.push("Conclusiones");
    return m;
  };

  const send = async () => {
    if (readOnly) return;
    const missing = requiredMissing();
    if (missing.length > 0) { toast.error(`Faltan campos: ${missing.join(", ")}`); return; }
    setSending(true);
    try {
      const savedId = await save({ silent: true });
      if (!savedId) throw new Error("No se pudo guardar");
      const r = await api.post(`/api/informes/${savedId}/complete`);
      toast.success(r.data.reenvio ? `Informe ${r.data.folio} reenviado` : `Informe ${r.data.folio} completado`);
      if (r.data.correo_enviado) toast.success("Correo enviado al cliente");
      else if (r.data.email_error) toast.error(`Correo no enviado: ${r.data.email_error}`);
      navigate("/tecnico/informes");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al enviar");
    } finally { setSending(false); }
  };

  const downloadPdf = async () => {
    const savedId = reportId || (await save({ silent: true }));
    if (!savedId) return;
    try {
      const r = await api.get(`/api/informes/${savedId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${folio || "informe"}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo se permiten imágenes"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Máximo 10MB por imagen"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/api/maintenance/images/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      ui("imagenes", [...inf.imagenes, r.data.id]);
      toast.success("Imagen subida");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir imagen"); }
  };

  const deleteImg = async (imgId) => {
    try {
      await api.delete(`/api/maintenance/images/${imgId}`);
      ui("imagenes", inf.imagenes.filter((x) => x !== imgId));
      toast.success("Imagen eliminada");
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">{folio || "Nuevo Informe Técnico"}</span>
            {estado === "completado" && <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Completado</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <span className="hidden sm:inline-block text-[11px] text-gray-500 mr-1">
                {saving
                  ? "Guardando…"
                  : lastSavedAt
                    ? `Guardado ${new Date(lastSavedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
                    : "Auto-guardado activo"}
              </span>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={() => save()} disabled={saving || sending}>
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Guardar
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/informes")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {readOnly && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 font-medium">
            🔒 Informe completado — Solo lectura. Un administrador puede editarlo.
          </div>
        )}

        {/* Cliente */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Datos del cliente</h2>
            <div>
              <Label>Cliente *</Label>
              <Select value={inf.cliente_id} onValueChange={selectClient} disabled={readOnly}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selectedClient?.direcciones?.length > 0 && (
              <div>
                <Label>Sucursal</Label>
                <Select value={inf.sucursal} onValueChange={(v) => {
                  const d = selectedClient.direcciones.find((x) => x.nombre === v);
                  ui("sucursal", v);
                  if (d) ui("direccion", `${d.direccion}, ${d.comuna}`);
                }} disabled={readOnly}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar (opcional)..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {selectedClient.direcciones.map((d) => (
                      <SelectItem key={d.nombre} value={d.nombre}>{d.nombre} — {d.direccion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Contacto</Label>
                <Input value={inf.contacto_nombre} onChange={(e) => ui("contacto_nombre", e.target.value)} autoComplete="name" className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Correo</Label>
                <Input type="email" inputMode="email" autoComplete="email" value={inf.contacto_email} onChange={(e) => ui("contacto_email", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Equipo */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-lg text-[#384091]">Equipo involucrado <span className="text-xs text-gray-400 font-normal">(opcional)</span></h2>
            {!readOnly && isAdmin && inf.cliente_id && (
              <EquipoSelect
                clienteId={inf.cliente_id}
                sucursal={inf.sucursal}
                onSelect={(e) => setInf((p) => ({
                  ...p,
                  marca: e.marca || p.marca,
                  modelo: e.modelo || p.modelo,
                  numero_serie: e.numero_serie || p.numero_serie,
                  numero_box: e.numero_box || p.numero_box,
                }))}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Marca</Label><Input value={inf.marca} onChange={(e) => ui("marca", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Modelo</Label><Input value={inf.modelo} onChange={(e) => ui("modelo", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>N° Serie</Label><Input value={inf.numero_serie} onChange={(e) => ui("numero_serie", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>N° Box</Label><Input value={inf.numero_box} onChange={(e) => ui("numero_box", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Contenido */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Contenido del informe</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Asunto del informe *</Label>
                <Input value={inf.titulo} onChange={(e) => ui("titulo", e.target.value)}
                  placeholder="Ej: Falla mayor en sistema hidráulico" className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Fecha del evento</Label>
                <Input type="date" value={inf.fecha_evento} onChange={(e) => ui("fecha_evento", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
            <div>
              <Label>Tipo de evento *</Label>
              <Select value={inf.tipo_evento} onValueChange={(v) => ui("tipo_evento", v)} disabled={readOnly}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {TIPOS_EVENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {SECCIONES.map(([label, key, ph]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Textarea rows={4} value={inf[key]} onChange={(e) => ui(key, e.target.value)}
                  placeholder={ph} className="mt-1 bg-white" disabled={readOnly} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Registro fotográfico */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#384091]">Registro fotográfico</h2>
              <Badge className="bg-blue-50 text-blue-700 border-0">{inf.imagenes.length} foto(s)</Badge>
            </div>
            <p className="text-xs text-gray-500">Puedes agregar todas las imágenes que necesites. Se optimizan automáticamente para el PDF.</p>
            {inf.imagenes.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {inf.imagenes.map((imgId, idx) => (
                  <div key={imgId} className="relative aspect-square rounded-lg border overflow-hidden">
                    <img src={`${api.defaults.baseURL}/api/maintenance/images/${imgId}`}
                      alt={`Imagen ${idx + 1}`} className="w-full h-full object-cover" />
                    {!readOnly && (
                      <button onClick={() => deleteImg(imgId)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!readOnly && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => cameraRef.current?.click()}
                  className="py-3 rounded-lg border-2 border-dashed border-[#0FB4B7]/40 flex items-center justify-center gap-2 text-[#0FB4B7] hover:bg-[#0FB4B7]/5">
                  <Camera className="h-4 w-4" /><span className="text-sm font-medium">Tomar foto</span>
                </button>
                <button onClick={() => galleryRef.current?.click()}
                  className="py-3 rounded-lg border-2 border-dashed border-[#384091]/40 flex items-center justify-center gap-2 text-[#384091] hover:bg-[#384091]/5">
                  <ImageIcon className="h-4 w-4" /><span className="text-sm font-medium">Cargar imagen</span>
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {!readOnly && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={downloadPdf} disabled={saving || sending}>
              <Download className="h-4 w-4 mr-1" /> Descargar PDF
            </Button>
            <Button className="flex-1 bg-[#384091] hover:bg-[#2D3375] text-white h-11"
              onClick={send} disabled={sending || saving}>
              {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
              {estado === "completado" ? "Regenerar y Reenviar" : "Completar y Enviar"}
            </Button>
          </div>
        )}
        {readOnly && (
          <Button variant="outline" className="w-full" onClick={downloadPdf}>
            <Download className="h-4 w-4 mr-1" /> Descargar PDF
          </Button>
        )}
      </main>
    </div>
  );
}
